import { BigNumber } from 'ethers'
import { useQuery } from '@tanstack/react-query'

import { getFeeDistributorContract } from '~/contracts/getFeeDistributorContract'
import { getSISContract } from '~/contracts/getSISContract'
import { getVotingEscrowContract } from '~/contracts/getVotingEscrowContract'
import { MAX_VESIS_WEEKS, WEEKS_IN_YEAR } from '~/lib/constants'
import { getWeeklySISDistribution } from '~/lib/getEstimateWeaklyRewards'
import { getVESISAPR } from '~/lib/getVESISAPR'
import { sisLockKeys } from '~/lib/queryKeys'
import { SimpleFraction } from '~/lib/SimpleFraction'
import { useVESISParams } from '~/hooks/useVESISParams'

interface Result {
	apr?: number
	lockDuration?: number
	totalRewards?: BigNumber
	veSISAmount?: BigNumber
	weeklyRewards?: BigNumber
}

export function useLockNewDetails(amount: BigNumber, duration: number): Result {
	const { config, provider } = useVESISParams()

	const { data } = useQuery({
		queryKey: sisLockKeys.newDetails(config.chain.id),
		queryFn: async () => {
			const feeDistributorContract = getFeeDistributorContract(
				config,
				provider
			)
			const votingEscrowContract = getVotingEscrowContract(
				config,
				provider
			)
			const sisContract = getSISContract(config, provider)
			const distribution = getWeeklySISDistribution(
				config,
				provider,
				feeDistributorContract
			)

			const [tokensPerWeek, totalVESISSupply, sisLocked] =
				await Promise.all([
					distribution,
					votingEscrowContract['totalSupply()'](),
					sisContract['balanceOf'](votingEscrowContract.address),
				])

			return {
				tokensPerWeek,
				totalVESISSupply,
				sisLocked,
			}
		},
	})

	if (!data) {
		return {}
	}

	const { tokensPerWeek, totalVESISSupply, sisLocked } = data

	const veSISAmount = new SimpleFraction(duration, MAX_VESIS_WEEKS).mul(
		amount
	)

	const newTotalVESISSupply = totalVESISSupply.add(veSISAmount.toBigNumber())

	let weaklyRewards: SimpleFraction
	if (newTotalVESISSupply.isZero()) {
		weaklyRewards = new SimpleFraction(0)
	} else {
		weaklyRewards = veSISAmount.mul(tokensPerWeek).div(newTotalVESISSupply)
	}

	const totalRewards = weaklyRewards.mul(duration)
	const lockDuration = duration / WEEKS_IN_YEAR

	const apr = getVESISAPR(weaklyRewards.toBigNumber(), amount)

	return {
		veSISAmount: veSISAmount.toBigNumber(),
		weeklyRewards: weaklyRewards.toBigNumber(),
		totalRewards: totalRewards.toBigNumber(),
		lockDuration,
		apr,
	}
}
